import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TableSessionsFetchAPI } from "../../api/TableSessions";
import { BusinessFetchAPI } from "../../api/Business";
import AlertSmall from "../Alerts/AlertSmall";
import {
  isActiveTableSessionStatus,
  normalizeTableSessionPayload,
  useTableSession,
} from "../../context/TableSessionContext";
import { enterTableSessionMenu } from "../../utils/tableSessionEntry";
import { buildCloudinaryImageUrl } from '../../utils/CloudinaryImageUrl';

const DEFAULT_BRAND_COLOR = "#111827";

const FINAL_STATUSES = ["APPROVED", "ACTIVE", "REJECTED", "EXPIRED", "CANCELLED", "CANCELED"];
const WAITING_STATUSES = ["PENDING", "WAITING", "REQUESTED"];
const POLLING_MS = 4000;

const getSafeMessage = (payload, fallback) =>
  payload?.safe_message ||
  payload?.message ||
  payload?.reason ||
  payload?.status_message ||
  fallback;

const isTableDisabled = (table) =>
  Boolean(table?.disabled || table?.is_disabled || table?.status === 0 || table?.available === false);

const isBranchUnavailable = (payload) => {
  const branch = payload?.branchoffice || payload?.branch || {};
  return (
    payload?.branch_available === false ||
    branch?.available === false ||
    branch?.ecommerce === 0 ||
    branch?.branchoffice_open === 0 ||
    branch?.state === 0
  );
};

const safeParse = (value, fallback = null) => {
  if (!value || value === "undefined" || value === "null") return fallback;
  try {
    return JSON.parse(value);
  } catch (err) {
    return fallback;
  }
};

const getLoggedInCustomer = () => {
  const storedClient = safeParse(localStorage.getItem("client"), null);
  const guestClient = localStorage.getItem("guestClient");
  const isLoggedIn =
    (guestClient === "0" || guestClient === 0) &&
    storedClient &&
    storedClient.name !== "Invitado";

  if (!isLoggedIn) return null;

  return {
    id: storedClient.id,
    code: storedClient.user_code,
    name: storedClient.name,
    email: storedClient.email,
  };
};

const TableEntryPage = () => {
  const { publicTableCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    requestTableAccess,
    refreshCurrentSession,
    status: contextStatus,
    session: contextSession,
  } = useTableSession();

  const [validating, setValidating] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [validation, setValidation] = useState(null);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [tableInUse, setTableInUse] = useState(false);
  const loggedInCustomer = useMemo(() => getLoggedInCustomer(), []);
  const [customerName, setCustomerName] = useState(loggedInCustomer?.name || "");
  const [customerNameError, setCustomerNameError] = useState("");
  const [businessConfig, setBusinessConfig] = useState(() => safeParse(localStorage.getItem("businessConfig"), null));
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem("logo") || "");
  const pollingRef = useRef(null);
  const requestCodeRef = useRef(null);
  const mountedRef = useRef(true);
  const brandFetchedForRef = useRef(null);

  const source = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = String(location.state?.source || params.get("source") || "").toUpperCase();
    return raw === "QR" || raw === "NFC" ? raw : undefined;
  }, [location.search, location.state]);

  const normalizedValidation = useMemo(
    () => normalizeTableSessionPayload(validation || {}),
    [validation]
  );

  const businessCode = normalizedValidation.business?.code;

  // Fetches logo + brand color for a first-time visitor (fresh browser scanning a table QR has
  // no "businessConfig" in localStorage yet - validatePublicTable's response only carries
  // business.id/code/name, never the branding). Runs once per business code.
  useEffect(() => {
    if (!businessCode || validating || error) return;
    if (brandFetchedForRef.current === businessCode) return;
    brandFetchedForRef.current = businessCode;

    let cancelled = false;
    (async () => {
      try {
        const response = await BusinessFetchAPI.getBusinessByCode(businessCode);
        const config = response?.data?.businessConfig;
        if (cancelled || !config) return;
        setBusinessConfig(config);
        localStorage.setItem("businessConfig", JSON.stringify(config));
        if (config.logo) {
          setLogoUrl(config.logo);
          localStorage.setItem("logo", config.logo);
        }
      } catch (err) {
        // Best effort - the page still works with default branding.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [businessCode, validating, error]);

  const clearPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const enterMenu = useCallback(async (session) => {
    await enterTableSessionMenu(session);
    navigate("/menu", { replace: true });
  }, [navigate]);

  const handleApproved = useCallback(async () => {
    clearPolling();
    const current = await refreshCurrentSession();
    if (current && isActiveTableSessionStatus(current.status)) {
      await enterMenu(current);
      return;
    }
    setError("La mesa fue aprobada, pero no se pudo recuperar la sesion actual.");
  }, [clearPolling, enterMenu, refreshCurrentSession]);

  const handleFinalStatus = useCallback(async (payload) => {
    const nextStatus = String(payload?.status || "").toUpperCase();
    setStatus(nextStatus);

    if (isActiveTableSessionStatus(nextStatus)) {
      await handleApproved();
      return true;
    }

    if (nextStatus === "REJECTED") {
      clearPolling();
      setMessage(getSafeMessage(payload, "La solicitud fue rechazada."));
      await refreshCurrentSession();
      return true;
    }

    if (nextStatus === "EXPIRED" || nextStatus === "CANCELLED" || nextStatus === "CANCELED") {
      clearPolling();
      AlertSmall("La solicitud de mesa expiro.", "warning");
      setMessage(getSafeMessage(payload, "La solicitud ya no esta activa."));
      await refreshCurrentSession();
      return true;
    }

    return FINAL_STATUSES.includes(nextStatus);
  }, [clearPolling, handleApproved, refreshCurrentSession]);

  const pollOnce = useCallback(async () => {
    try {
      if (requestCodeRef.current) {
        const requestStatus = await TableSessionsFetchAPI.getRequestStatus();
        if (!mountedRef.current) return;
        const handled = await handleFinalStatus(requestStatus);
        if (handled) return;
        setStatus(String(requestStatus?.status || "PENDING").toUpperCase());
        setMessage(getSafeMessage(requestStatus, "Esperando aprobacion."));
        return;
      }

      const current = await refreshCurrentSession();
      if (!mountedRef.current) return;
      if (current?.status) {
        const handled = await handleFinalStatus(current);
        if (!handled) setStatus(String(current.status).toUpperCase());
      }
    } catch (err) {
      if (mountedRef.current) {
        setMessage("Seguimos esperando respuesta de la sucursal.");
      }
    }
  }, [handleFinalStatus, refreshCurrentSession]);

  const startPolling = useCallback(() => {
    clearPolling();
    pollingRef.current = setInterval(pollOnce, POLLING_MS);
  }, [clearPolling, pollOnce]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearPolling();
    };
  }, [clearPolling]);

  useEffect(() => {
    const validate = async () => {
      setValidating(true);
      setError("");
      setMessage("");

      try {
        const current = await refreshCurrentSession();
        if (current && isActiveTableSessionStatus(current.status)) {
          await enterMenu(current);
          return;
        }

        if (current && WAITING_STATUSES.includes(String(current.status || "").toUpperCase())) {
          setStatus("PENDING");
          requestCodeRef.current = true;
          setMessage("Esperando aprobacion.");
          startPolling();
        }

        const currentRequest = await TableSessionsFetchAPI.getRequestStatus();
        if (currentRequest?.status) {
          const handled = await handleFinalStatus(currentRequest);
          if (!handled) {
            requestCodeRef.current = true;
            setStatus(String(currentRequest.status || "PENDING").toUpperCase());
            setMessage(getSafeMessage(currentRequest, "Esperando aprobacion."));
            startPolling();
          }
        }

        const response = await TableSessionsFetchAPI.validatePublicTable(publicTableCode);
        if (!mountedRef.current) return;
        setValidation(response);

        const normalized = normalizeTableSessionPayload(response);
        if (response?.valid === false || response?.exists === false) {
          setError(getSafeMessage(response, "El codigo de mesa no es valido."));
        } else if (isTableDisabled(normalized.table)) {
          setError("Esta mesa no esta disponible.");
        } else if (isBranchUnavailable(response)) {
          setError("La sucursal no esta disponible para ecommerce.");
        } else {
          // Pre-warms business/branchoffice/machine context (no navigation) so the "Solicitar
          // acceso" screen and its API calls already have the right business scope.
          await enterTableSessionMenu(normalized);
        }
      } catch (err) {
        setError("No fue posible validar este codigo de mesa.");
      } finally {
        if (mountedRef.current) setValidating(false);
      }
    };

    validate();
  }, [enterMenu, handleFinalStatus, publicTableCode, refreshCurrentSession, startPolling]);

  // Note: this page intentionally has no socket connection of its own. While a request is PENDING
  // there is no session/participant cookie yet, so the backend's cookie-based room join
  // (src/sockets.js#joinTableSessionRoom on smartpay-dashboard) never places this device in any
  // room - a socket opened here would never receive tableSessionApproved/tableSessionRejected/etc
  // regardless of what query params it sent (the server ignores handshake.query for table-session
  // purposes on purpose, to avoid trusting a client-supplied session id). The polling below is the
  // real mechanism that detects PENDING -> APPROVED/REJECTED, and per Fase 12 it is fine to keep it
  // for exactly this window. Once APPROVED (participant cookie set) and the app navigates into
  // /menu, TableSessionProvider's own socket (context/TableSessionContext.jsx) takes over and is
  // the one real connection for everything that happens afterwards.

  const onRequestAccess = async () => {
    if (requesting || WAITING_STATUSES.includes(String(status || "").toUpperCase())) return;

    const trimmedName = customerName.trim();
    if (!trimmedName) {
      setCustomerNameError("Ingresa tu nombre para solicitar la mesa.");
      return;
    }
    setCustomerNameError("");

    const customer = loggedInCustomer
      ? { ...loggedInCustomer, name: trimmedName }
      : { name: trimmedName };

    setRequesting(true);
    setError("");
    setTableInUse(false);
    setMessage("Esperando aprobacion.");
    setStatus("PENDING");

    try {
      const response = await requestTableAccess({ publicTableCode, source, customer });
      if (!mountedRef.current) return;
      requestCodeRef.current = true;
      const handled = await handleFinalStatus(response);
      if (!handled) startPolling();
    } catch (err) {
      setStatus(null);
      // Fase 7: a different device already holds this table's session. Never leak who, how many,
      // or what they ordered - the only path back in for this device is a real invitation from one
      // of them.
      if (err?.response?.status === 409 && err?.response?.data?.code === "TABLE_ALREADY_IN_USE") {
        setTableInUse(true);
      } else {
        setError("No fue posible crear la solicitud de acceso.");
      }
    } finally {
      if (mountedRef.current) setRequesting(false);
    }
  };

  const canRequest =
    !validating &&
    !error &&
    !requesting &&
    !WAITING_STATUSES.includes(String(status || "").toUpperCase()) &&
    !isActiveTableSessionStatus(contextStatus);

  const tableLabel = normalizedValidation.table.name || normalizedValidation.table.number || "Mesa";
  const restaurantName = normalizedValidation.business?.name || normalizedValidation.businessName || "Restaurante";
  const branchName = normalizedValidation.branchoffice?.name || normalizedValidation.branchofficeName || "Sucursal";
  const isWaiting = WAITING_STATUSES.includes(String(status || "").toUpperCase());
  const brandColor = businessConfig?.containerColor || DEFAULT_BRAND_COLOR;
  const restaurantInitial = (restaurantName || "S").trim().charAt(0).toUpperCase();
  const showWelcomeDetails = !tableInUse && !error;

  return (
    <div className="init-x-page" style={{ "--init-brand": brandColor }}>
      <div className="init-x-card">
        <div className="init-x-brandbar" aria-hidden="true" />

        {validating ? (
          <div className="init-x-loading">
            <span className="init-x-spinner" aria-hidden="true" />
            <p className="mb-0">Estamos preparando tu mesa...</p>
          </div>
        ) : (
          <>
            <div className="init-x-header">
              <div className="init-x-logo">
                {logoUrl ? (
                  <img src={buildCloudinaryImageUrl({ url: logoUrl, width: 300, quality: "auto", type: "image" }) || undefined} alt={restaurantName} />
                ) : (
                  <span className="init-x-logo-fallback">{restaurantInitial}</span>
                )}
              </div>

              <p className="init-x-eyebrow">SmartPay Mesa</p>

              <h1 className="init-x-title">
                {showWelcomeDetails ? `¡Hola! Bienvenido a ${restaurantName}` : restaurantName}
              </h1>

              {showWelcomeDetails ? (
                <>
                  <p className="init-x-subtitle">Vas a pedir desde esta mesa. Solo nos falta tu nombre.</p>
                  <div className="init-x-chips">
                    <span className="init-x-chip">Sucursal: <strong>{branchName}</strong></span>
                    <span className="init-x-chip">Mesa: <strong>{tableLabel}</strong></span>
                    {normalizedValidation.table.environment ? (
                      <span className="init-x-chip">Ambiente: <strong>{normalizedValidation.table.environment}</strong></span>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>

            <div className="init-x-body">
              {tableInUse ? (
                <div className="init-x-alert">
                  <p>Esta mesa ya tiene una sesión activa.</p>
                  <p>Para participar en el pedido, solicita a uno de los integrantes que genere una invitación desde su teléfono.</p>
                </div>
              ) : error ? (
                <div className="init-x-alert">{error}</div>
              ) : (
                <>
                  {canRequest ? (
                    <>
                      <span className="init-x-step-tag">Antes de pedir</span>
                      <label htmlFor="customerName" className="init-x-label">
                        Dame tu nombre para poder pedir
                      </label>
                      <input
                        id="customerName"
                        type="text"
                        className={`init-x-input${customerNameError ? " is-invalid" : ""}`}
                        placeholder="Escribe tu nombre"
                        value={customerName}
                        onChange={(e) => {
                          setCustomerName(e.target.value);
                          if (customerNameError) setCustomerNameError("");
                        }}
                      />
                      {customerNameError ? <div className="init-x-error">{customerNameError}</div> : null}
                    </>
                  ) : null}

                  <button
                    type="button"
                    className={`init-x-submit${isWaiting ? " is-waiting" : ""}`}
                    disabled={!canRequest}
                    onClick={onRequestAccess}
                  >
                    {isWaiting ? (
                      <>
                        <span className="init-x-spinner" aria-hidden="true" />
                        Esperando aprobación
                      </>
                    ) : (
                      "Solicitar acceso a esta mesa"
                    )}
                  </button>

                  {message ? <div className="init-x-status">{message}</div> : null}
                  {contextSession && isActiveTableSessionStatus(contextStatus) ? (
                    <div className="init-x-status is-success">Sesión de mesa activa.</div>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TableEntryPage;
